import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { Sparkles, AlertCircle } from 'lucide-react';
import { useStrategyStore } from '@/stores/strategy';
import { SwimLane, type ContainerKind, type SwimLaneData } from './SwimLane';
import {
  SwimLaneFilterBar,
  type OrganizeByMode,
} from './SwimLaneFilterBar';
import { SWIM_LANE_SPRING } from './animation';

interface SwimLaneBoardProps {
  clientUid: string;
  personaId?: string;
  onOpenBrief: (briefId: string) => void;
  onOpenCreative?: (creativeId: string) => void;
  onAddBrief: (lane: SwimLaneData) => void;
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function SkeletonLane() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded bg-slate-200 animate-pulse" />
        <div className="h-4 w-40 rounded bg-slate-200 animate-pulse" />
        <div className="ml-auto h-3 w-16 rounded bg-slate-200 animate-pulse" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-slate-100 rounded-xl h-[140px] animate-pulse"
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SwimLaneBoard
// ---------------------------------------------------------------------------

export function SwimLaneBoard({
  clientUid,
  personaId,
  onOpenBrief,
  onOpenCreative,
  onAddBrief,
}: SwimLaneBoardProps) {
  const {
    lanes,
    isSeed,
    loading,
    error,
    container,
    organizeBy,
    setContainer,
    setOrganizeBy,
    fetchLanes,
    triggerRecompute,
    recomputeStatus,
  } = useStrategyStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch on mount + whenever client/persona/organize_by changes
  useEffect(() => {
    if (!clientUid) return;
    void fetchLanes(clientUid, personaId);
  }, [clientUid, personaId, organizeBy, fetchLanes]);

  // Apply local filters (search/status) to each lane's items
  const filteredLanes = useMemo<SwimLaneData[]>(() => {
    if (!search && !statusFilter) return lanes;
    const q = search.trim().toLowerCase();
    return lanes.map((lane) => {
      const items = lane.items.filter((item) => {
        if (statusFilter && item.status !== statusFilter) return false;
        if (!q) return true;
        const haystack = [
          item.name,
          item.id,
          item.persona_name,
          item.persona_id,
          item.primary_text,
          item.hook_type,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
      return { ...lane, items };
    });
  }, [lanes, search, statusFilter]);

  const totalAll = useMemo(
    () => lanes.reduce((acc, l) => acc + l.items.length, 0),
    [lanes],
  );
  const totalShown = useMemo(
    () => filteredLanes.reduce((acc, l) => acc + l.items.length, 0),
    [filteredLanes],
  );

  const showAutoOrganize =
    organizeBy !== 'strategy' && !loading && lanes.length > 0;

  return (
    <div className="space-y-3 relative">
      <SwimLaneFilterBar
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        totalShown={totalShown}
        totalAll={totalAll}
        container={container}
        onContainerChange={(c) => setContainer(c)}
        organizeBy={organizeBy}
        onOrganizeByChange={(mode: OrganizeByMode) => setOrganizeBy(mode)}
        isSeed={isSeed}
      />

      {/* Seed banner (Strategy mode only) */}
      <AnimatePresence>
        {organizeBy === 'strategy' && isSeed && lanes.length > 0 && (
          <motion.div
            key="seed-banner"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800"
          >
            <Sparkles size={12} className="text-amber-500 flex-shrink-0" />
            <span className="flex-1">
              Using seeded strategy clusters — click <strong>Re-run analysis</strong> to
              generate live clusters from your account data.
            </span>
            <button
              type="button"
              onClick={() => triggerRecompute(clientUid)}
              disabled={recomputeStatus === 'running' || recomputeStatus === 'queued'}
              className="px-2.5 py-1 text-[11px] font-semibold bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50 transition"
            >
              {recomputeStatus === 'running' || recomputeStatus === 'queued'
                ? 'Running…'
                : 'Re-run analysis'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium">Could not load swim lanes.</div>
            <div className="text-xs text-rose-600">{error}</div>
          </div>
          <button
            type="button"
            onClick={() => fetchLanes(clientUid, personaId)}
            className="text-xs font-medium text-rose-700 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Body */}
      {loading && lanes.length === 0 ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <SkeletonLane key={i} />
          ))}
        </div>
      ) : (
        <LayoutGroup>
          <motion.div layout className="space-y-3">
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredLanes.map((lane) => (
                <motion.div
                  key={lane.lane_id}
                  layout
                  layoutId={`lane-${lane.lane_type}-${lane.lane_id}`}
                  initial={{ opacity: 0, y: 8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={SWIM_LANE_SPRING}
                >
                  <SwimLane
                    lane={lane}
                    container={container}
                    onOpenBrief={onOpenBrief}
                    onOpenCreative={onOpenCreative}
                    onAddBrief={onAddBrief}
                    onRequestRecompute={
                      lane.lane_type === 'strategy'
                        ? () => triggerRecompute(clientUid)
                        : undefined
                    }
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </LayoutGroup>
      )}

      {/* Auto-organize floating CTA */}
      <AnimatePresence>
        {showAutoOrganize && (
          <motion.button
            key="auto-organize"
            type="button"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOrganizeBy('strategy')}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 bg-teal-500 text-white text-sm font-semibold rounded-full shadow-lg hover:bg-teal-600 transition-colors"
          >
            <Sparkles size={14} />
            Auto-organize
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
